import { useState, useEffect } from 'react';
import axios from 'axios';
import { Layout } from '../../components/layout/Layout';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { toast } from 'sonner';
import { 
  Users, Plus, Shield, ShieldCheck, ShieldOff, UserCog, Trash2, 
  ArrowLeft, Mail, Lock, User, Crown, Headphones, DollarSign, Eye,
  CheckCircle, XCircle, MoreVertical, Edit2
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { format } from 'date-fns';

const API_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

const ADMIN_ROLES = [
  { 
    value: 'super_admin', 
    label: 'Super Admin', 
    icon: Crown,
    color: 'bg-red-500',
    description: 'Full access to all features and settings',
    permissions: ['All permissions']
  },
  { 
    value: 'support', 
    label: 'Support', 
    icon: Headphones,
    color: 'bg-blue-500',
    description: 'Handle user issues and ticket support',
    permissions: ['View users', 'Manage users', 'View tickets', 'Manage tickets', 'View events']
  },
  { 
    value: 'finance', 
    label: 'Finance', 
    icon: DollarSign,
    color: 'bg-green-500',
    description: 'Manage transactions and subscriptions',
    permissions: ['View transactions', 'Manage transactions', 'View subscriptions', 'Manage subscriptions', 'View users']
  },
  { 
    value: 'moderator', 
    label: 'Moderator', 
    icon: Eye,
    color: 'bg-yellow-500',
    description: 'Approve events and moderate content',
    permissions: ['View events', 'Approve events', 'Reject events', 'View users', 'Manage media']
  },
];

export default function AdminUsers() {
  const { user } = useAuth();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialog, setCreateDialog] = useState(false);
  const [editDialog, setEditDialog] = useState({ open: false, admin: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, admin: null });
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    admin_role: 'moderator'
  });
  const [editRole, setEditRole] = useState('');

  useEffect(() => {
    fetchAdmins();
  }, []);

  const fetchAdmins = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/users/admins`);
      setAdmins(response.data);
    } catch (error) {
      console.error('Error fetching admins:', error);
      if (error.response?.status === 403) {
        toast.error('Access denied. Super Admin privileges required.');
      } else {
        toast.error('Failed to load admin users');
      }
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      email: '',
      password: '',
      name: '',
      admin_role: 'moderator'
    });
  };

  const handleCreate = async () => {
    if (!formData.email || !formData.password || !formData.name) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (formData.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/admin/users/create-admin`, formData);
      toast.success('Admin user created successfully');
      setCreateDialog(false);
      resetForm();
      fetchAdmins();
    } catch (error) {
      console.error('Error creating admin:', error);
      toast.error(error.response?.data?.detail || 'Failed to create admin user');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateRole = async () => {
    if (!editRole) return;

    setSubmitting(true);
    try {
      await axios.put(
        `${API_URL}/api/admin/users/${editDialog.admin.user_id}/admin-role`,
        { admin_role: editRole },
        
      );
      toast.success('Admin role updated successfully');
      setEditDialog({ open: false, admin: null });
      fetchAdmins();
    } catch (error) {
      console.error('Error updating admin:', error);
      toast.error(error.response?.data?.detail || 'Failed to update admin role');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    setSubmitting(true);
    try {
      await axios.delete(
        `${API_URL}/api/admin/users/${deleteDialog.admin.user_id}`,
        
      );
      toast.success('Admin user deleted');
      setDeleteDialog({ open: false, admin: null });
      fetchAdmins();
    } catch (error) {
      console.error('Error deleting admin:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete admin user');
    } finally {
      setSubmitting(false);
    }
  };

  const getRoleInfo = (role) => {
    return ADMIN_ROLES.find(r => r.value === role) || ADMIN_ROLES[3];
  };

  const openEditDialog = (admin) => {
    setEditRole(admin.admin_role || 'moderator');
    setEditDialog({ open: true, admin });
  };

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-4 md:px-8 py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Link to="/admin">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
          <div className="flex-1">
            <h1 className="text-3xl font-bold">Admin User Management</h1>
            <p className="text-muted-foreground">Create and manage admin accounts with different access levels</p>
          </div>
          <Button onClick={() => setCreateDialog(true)} data-testid="create-admin-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Admin
          </Button>
        </div>

        {/* Role Overview Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {ADMIN_ROLES.map((role) => {
            const RoleIcon = role.icon;
            const count = admins.filter(a => a.admin_role === role.value).length;
            return (
              <Card key={role.value} className="relative overflow-hidden">
                <div className={`absolute top-0 left-0 w-1 h-full ${role.color}`} />
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${role.color}/10`}>
                        <RoleIcon className={`w-5 h-5 ${role.color.replace('bg-', 'text-')}`} />
                      </div>
                      <div>
                        <p className="font-semibold">{role.label}</p>
                        <p className="text-xs text-muted-foreground">{count} user{count !== 1 ? 's' : ''}</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Admin Users List */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              Admin Users ({admins.length})
            </CardTitle>
            <CardDescription>
              All users with administrative access to the platform
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-4">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="h-20 skeleton rounded-lg" />
                ))}
              </div>
            ) : admins.length > 0 ? (
              <div className="space-y-3">
                {admins.map((admin) => {
                  const roleInfo = getRoleInfo(admin.admin_role);
                  const RoleIcon = roleInfo.icon;
                  const isCurrentUser = admin.user_id === user?.user_id;
                  
                  return (
                    <div
                      key={admin.user_id}
                      className={`flex items-center gap-4 p-4 rounded-xl border ${
                        isCurrentUser ? 'border-primary/30 bg-primary/5' : 'border-border'
                      }`}
                      data-testid={`admin-user-${admin.user_id}`}
                    >
                      {/* Avatar */}
                      <div className={`w-12 h-12 rounded-full ${roleInfo.color} flex items-center justify-center text-white font-bold text-lg`}>
                        {admin.name?.charAt(0).toUpperCase() || 'A'}
                      </div>

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h4 className="font-semibold">{admin.name}</h4>
                          {isCurrentUser && (
                            <Badge variant="outline" className="text-xs">You</Badge>
                          )}
                          <Badge className={`${roleInfo.color} text-white`}>
                            <RoleIcon className="w-3 h-3 mr-1" />
                            {roleInfo.label}
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground truncate">{admin.email}</p>
                        <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                          {admin.two_fa_enabled ? (
                            <span className="flex items-center gap-1 text-green-500">
                              <ShieldCheck className="w-3 h-3" /> 2FA Enabled
                            </span>
                          ) : (
                            <span className="flex items-center gap-1 text-yellow-500">
                              <ShieldOff className="w-3 h-3" /> 2FA Not Setup
                            </span>
                          )}
                          {admin.created_at && (
                            <span>Added {format(new Date(admin.created_at), 'MMM d, yyyy')}</span>
                          )}
                        </div>
                      </div>

                      {/* Actions */}
                      {!isCurrentUser && (
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openEditDialog(admin)}
                            data-testid={`edit-admin-${admin.user_id}`}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-destructive"
                            onClick={() => setDeleteDialog({ open: true, admin })}
                            data-testid={`delete-admin-${admin.user_id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-12">
                <UserCog className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">No admin users found</h3>
                <p className="text-muted-foreground mb-4">
                  Create admin accounts to help manage your platform.
                </p>
                <Button onClick={() => setCreateDialog(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add First Admin
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Create Admin Dialog */}
        <Dialog open={createDialog} onOpenChange={setCreateDialog}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <UserCog className="w-5 h-5" />
                Create Admin User
              </DialogTitle>
              <DialogDescription>
                Add a new administrator with specific access permissions.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="name">Full Name *</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="name"
                    placeholder="John Doe"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="pl-10"
                    data-testid="admin-name-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email Address *</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="support@nextgig.shop"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="pl-10"
                    data-testid="admin-email-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password *</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="Minimum 8 characters"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="pl-10"
                    data-testid="admin-password-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Admin Role *</Label>
                <Select
                  value={formData.admin_role}
                  onValueChange={(value) => setFormData({ ...formData, admin_role: value })}
                >
                  <SelectTrigger data-testid="admin-role-select">
                    <SelectValue placeholder="Select a role" />
                  </SelectTrigger>
                  <SelectContent>
                    {ADMIN_ROLES.map((role) => {
                      const RoleIcon = role.icon;
                      return (
                        <SelectItem key={role.value} value={role.value}>
                          <div className="flex items-center gap-2">
                            <RoleIcon className={`w-4 h-4 ${role.color.replace('bg-', 'text-')}`} />
                            <span>{role.label}</span>
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>

              {/* Role Description */}
              {formData.admin_role && (
                <Alert className="bg-muted/50">
                  <AlertDescription>
                    <div className="space-y-2">
                      <p className="text-sm font-medium">
                        {getRoleInfo(formData.admin_role).description}
                      </p>
                      <div className="flex flex-wrap gap-1">
                        {getRoleInfo(formData.admin_role).permissions.map((perm, i) => (
                          <Badge key={i} variant="outline" className="text-xs">
                            {perm}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </AlertDescription>
                </Alert>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => { setCreateDialog(false); resetForm(); }}>
                Cancel
              </Button>
              <Button onClick={handleCreate} disabled={submitting} data-testid="create-admin-submit-btn">
                {submitting ? 'Creating...' : 'Create Admin'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Role Dialog */}
        <Dialog open={editDialog.open} onOpenChange={(open) => !open && setEditDialog({ open: false, admin: null })}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Change Admin Role</DialogTitle>
              <DialogDescription>
                Update the role and permissions for {editDialog.admin?.name}
              </DialogDescription>
            </DialogHeader>

            <div className="py-4">
              <Label>New Role</Label>
              <Select value={editRole} onValueChange={setEditRole}>
                <SelectTrigger className="mt-2" data-testid="edit-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ADMIN_ROLES.map((role) => {
                    const RoleIcon = role.icon;
                    return (
                      <SelectItem key={role.value} value={role.value}>
                        <div className="flex items-center gap-2">
                          <RoleIcon className={`w-4 h-4 ${role.color.replace('bg-', 'text-')}`} />
                          <span>{role.label}</span>
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>

              {editRole && (
                <Alert className="mt-4 bg-muted/50">
                  <AlertDescription>
                    <p className="text-sm">{getRoleInfo(editRole).description}</p>
                  </AlertDescription>
                </Alert>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setEditDialog({ open: false, admin: null })}>
                Cancel
              </Button>
              <Button onClick={handleUpdateRole} disabled={submitting} data-testid="update-role-btn">
                {submitting ? 'Updating...' : 'Update Role'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialog.open} onOpenChange={(open) => !open && setDeleteDialog({ open: false, admin: null })}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-destructive">
                <Trash2 className="w-5 h-5" />
                Delete Admin User
              </DialogTitle>
              <DialogDescription>
                Are you sure you want to remove admin access for <strong>{deleteDialog.admin?.name}</strong>? 
                This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteDialog({ open: false, admin: null })}>
                Cancel
              </Button>
              <Button 
                variant="destructive" 
                onClick={handleDelete} 
                disabled={submitting}
                data-testid="confirm-delete-admin-btn"
              >
                {submitting ? 'Deleting...' : 'Delete Admin'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
